import type { DailyTask } from '../lib/dailyApi';

/**
 * Daily-summary task utilities (TASK-FE-DAILY-006).
 *
 * Grouping is performed server-side; these helpers handle the within-group
 * presentation: splitting My Tasks vs Team Tasks and sorting by project
 * (FR-DAILY-008..012). Row rendering itself is the shared
 * `components/tasks/TaskListRow`.
 */

/** Resolve a display name for a task's project. */
export function getProjectName(task: DailyTask): string {
  return task.projectName ?? 'No project';
}

/**
 * Internal CCC route to a task's detail page (CLE-274), e.g. "/tasks/CLE-184"
 * or "/tasks/<uuid>". The canonical task-detail route is project-independent
 * (`/tasks/:taskIdentifier`) and resolves the human identifier (or UUID) to a
 * task on its own — so any task, anywhere in the app, can link to it without
 * needing a projectId. We prefer the readable `identifier`, falling back to the
 * `id`. Returns null only when neither a usable id nor identifier exists, so
 * callers can render a non-clickable row instead of a broken link.
 */
export function taskDetailPath(
  task: Pick<DailyTask, 'id' | 'identifier'> | { id?: string | null; identifier?: string | null },
): string | null {
  const ref = task.identifier ?? task.id;
  if (!ref) return null;
  return `/tasks/${encodeURIComponent(ref)}`;
}

/** Sort tasks by project name, keeping stable order for the same project. */
export function sortTasksByProject(tasks: DailyTask[]): DailyTask[] {
  return [...tasks].sort((a, b) => getProjectName(a).localeCompare(getProjectName(b)));
}

/** Split a group's tasks into the current user's tasks vs everyone else's. */
export function splitMineVsTeam(
  tasks: DailyTask[],
  currentUserId: string,
): { mine: DailyTask[]; team: DailyTask[] } {
  const mine: DailyTask[] = [];
  const team: DailyTask[] = [];
  for (const task of tasks) {
    if (task.assignee?.id === currentUserId) mine.push(task);
    else team.push(task);
  }
  return { mine: sortTasksByProject(mine), team: sortTasksByProject(team) };
}
